import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import type { Portal } from "@/types";

export interface ELKCredentials {
  kibana_url: string;
  space_id: string;
  user: string;
  password: string;
}

export const useLogViewer = (portal: Portal) => {
  const queryClient = useQueryClient();
  const portalId = portal.id;

  // Check if ELK is already provisioned from portal settings
  const elkConfig = portal.settings
    ? {
        kibana_url: portal.settings.elk_kibana_url,
        space_id: portal.settings.elk_space_id,
        user: portal.settings.elk_user,
        password: portal.settings.elk_password,
      }
    : null;

  const isEnabled =
    !!elkConfig?.kibana_url && !!elkConfig?.user && !!elkConfig?.password;

  // Mutation: provision ELK access
  const enableMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ELKCredentials>(
        API_ENDPOINTS.SYSTEM.LOG_VIEWER.PROVISION(portalId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTALS.DETAIL(portalId),
      });
    },
  });

  // Mutation: reset ELK password
  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ELKCredentials>(
        API_ENDPOINTS.SYSTEM.LOG_VIEWER.RESET_PASSWORD(portalId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTALS.DETAIL(portalId),
      });
    },
  });

  // Mutation: disable ELK access
  const disableMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(
        API_ENDPOINTS.SYSTEM.LOG_VIEWER.DEPROVISION(portalId)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTALS.DETAIL(portalId),
      });
    },
  });

  return {
    elkConfig,
    isEnabled,

    enableLogViewer: enableMutation.mutateAsync,
    isEnabling: enableMutation.isPending,

    resetPassword: resetPasswordMutation.mutateAsync,
    isResetting: resetPasswordMutation.isPending,

    disableLogViewer: disableMutation.mutate,
    isDisabling: disableMutation.isPending,
  };
};
